<template>
  <div class="team-licenses-embed" :style="containerStyle">
    <div v-if="!iframeUrl" class="text-muted small">
      Team-management embed URL is not configured for this environment
      (set <code>VUE_APP_TEAM_MANAGEMENT_URL</code>). The CS licence view
      will appear here once the env is wired up.
    </div>
    <div v-else ref="zoidContainer" data-testid="team-licenses-zoid-container" />
  </div>
</template>

<script>
// Auth: forward this app's own admin token (ahaTokenAdmin) as the embed's Bearer.
// The embed would otherwise fall back to the shared ahaToken cookie, but 55broadway
// login never establishes that cookie (the cross-origin login XHR runs without
// credentials, so its Set-Cookie is dropped) — leaving the embed 401 in a fresh
// session and 403 when a backdoor has swapped ahaToken for a customer's. ahaTokenAdmin
// is always present after login and is the staff identity, so it fixes both.

import Vue from 'vue'
import { mapState } from 'vuex'
import Config from '@/config'
import TeamAppIframe from '@/plugins/zoid/teamAppZoid'

export default {
  name: 'TeamLicensesEmbed',
  props: {
    accountId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      // Tracks the height the embed reports via `onHeightChange`. Must
      // live in `data()` so Vue 2's reactivity wires it up — without
      // this declaration, `this.iframeHeight = X` would create a plain
      // (non-reactive) property and `containerStyle` would never
      // recompute.
      iframeHeight: null,
    }
  },
  computed: {
    ...mapState(['currentUser']),
    // Inline height on the wrapper. Before the embed reports a height,
    // we floor at 600px so the zoid wrapper inside has somewhere to
    // render (CSS percentages on the children require an explicit
    // pixel height on the parent — `min-height` alone does NOT satisfy
    // that, which is what makes the iframe render blank). Once
    // `onHeightChange` fires we switch to the reported value so the
    // iframe sizes itself to its content.
    containerStyle() {
      return this.iframeHeight
        ? { height: `${this.iframeHeight}px` }
        : { height: '600px' }
    },
    iframeUrl() {
      if (!Config.teamManagementAppUrl) return null
      const base = Config.teamManagementAppUrl.replace(/\/$/, '')
      return `${base}/admin/teams/${this.accountId}`
    },
    token() {
      return Vue.cookie.get('ahaTokenAdmin')
    },
    currentUserProps() {
      return {
        id: this.currentUser?.id,
        email: this.currentUser?.email,
        name: [this.currentUser?.firstName, this.currentUser?.lastName]
          .filter(Boolean)
          .join(' ') || this.currentUser?.email,
        // Synthesised because vuex isn't guaranteed to carry one. Only satisfies the embed's client-side
        // role-gate; the API still enforces adminUpRoles on every route it calls.
        role: 'admin',
      }
    },
  },
  watch: {
    accountId: {
      handler: 'renderZoid',
    },
  },
  mounted() {
    this.renderZoid()
  },
  beforeDestroy() {
    this.closeZoid()
  },
  methods: {
    async closeZoid() {
      if (this.zoidInstance) {
        await this.zoidInstance.close()
        this.zoidInstance = null
      }
    },
    async renderZoid() {
      // Capture the accountId at entry and re-check after the close
      // await. If the prop has changed under us mid-flight (the watcher
      // re-fired before we resumed), bail — the newer renderZoid call
      // will own the next instance. Without this guard, two iframes
      // can race to mount, or zoid throws on the second .render().
      const renderingFor = this.accountId
      await this.closeZoid()
      if (renderingFor !== this.accountId) return
      if (!this.iframeUrl || !this.$refs.zoidContainer) return
      this.zoidInstance = TeamAppIframe({
        url: this.iframeUrl,
        token: this.token,
        currentUser: this.currentUserProps,
        onHeightChange: this.onHeightChange,
      })
      this.zoidInstance.render(this.$refs.zoidContainer)
    },
    onHeightChange(height) {
      this.iframeHeight = typeof height === 'number' && height > 0 ? height : null
    },
  },
}
</script>

<style lang="scss" scoped>
.team-licenses-embed {
  position: relative;
  width: 100%;
  // Height comes from `containerStyle` (inline) — either 600px floor or
  // the embed-reported height. Don't set `min-height` here too, or it
  // would race the inline value.

  // Mirror the presenter pattern: stretch BOTH the ref'd inner div
  // (direct child) and the zoid wrapper (a deeper descendant) to 100%
  // of the container. The `> div` rule is the load-bearing one — zoid
  // appends its wrapper inside the ref'd div, and unless that div has
  // an explicit height, the wrapper computes to 0px and the iframe
  // never paints.
  ::v-deep > div,
  ::v-deep div[id^='zoid-team-app-iframe-'] {
    width: 100% !important;
    height: 100% !important;
    display: block !important;
  }

  ::v-deep iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
  }
}
</style>
